import { useCallback, useEffect, useState } from "react";
import { MailCheck, RefreshCw } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { OUTBOUND_REPLIES_URL, outboundReplyIntentUrl } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Btn, INTENT_TONE, inputCls, OutboundHeader, Pill, useOpenLead } from "./ui";

/**
 * Outbound -> Replies. Mail is read only when someone presses Check replies
 * (plus a check on one prospect just before their follow-up goes). Bounces,
 * "stop" and out-of-office are decided by rules on the spot; the rest is
 * classified by the AI Worker, or here by hand. Interested / meeting /
 * question replies become leads.
 */

const FILTERS = [["", "All"], ["unclassified", "To classify"], ["interested,meeting,question", "Positive"],
    ["not_now", "Not now"], ["not_interested", "Not interested"], ["unsubscribe,bounce", "Opt-outs"]];

export default function Replies() {
    const { fetch, create } = useApiMethods();
    const [intent, setIntent] = useState("");
    const [data, setData] = useState(null);
    const [checking, setChecking] = useState(false);
    const openLead = useOpenLead();

    const load = useCallback(() => {
        fetch(OUTBOUND_REPLIES_URL, { intent }).then(res => {
            if (res?.status === "success") setData(res);
            else ToastMsg("error", res?.message || "Could not load replies");
        });
    }, [intent]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const check = async () => {
        setChecking(true);
        try {
            const res = await create(OUTBOUND_REPLIES_URL, {});
            if (res?.status === "success") {
                ToastMsg("success", res.message);
                (res.data?.errors || []).forEach(e => ToastMsg("warning", e));
                load();
            } else ToastMsg("error", res?.message || "Could not check replies");
        } finally { setChecking(false); }
    };
    const setReplyIntent = async (r, value) => {
        const res = await create(outboundReplyIntentUrl(r.id), { intent: value });
        if (res?.status === "success") {
            ToastMsg("success", res.lead_id ? `Marked ${value}. Lead #${res.lead_id} is in the CRM.` : `Marked ${value}.`);
            load();
        } else ToastMsg("error", res?.message || "Could not update");
    };

    return (
        <ContainerCard>
            <div className="p-4 space-y-4 overflow-auto">
                <OutboundHeader icon={MailCheck} title="Replies"
                    subtitle={`Answers from prospects. ${data?.last_checked ? `Last checked ${data.last_checked}.` : "Not checked yet."}`}>
                    <Btn variant="primary" onClick={check} disabled={checking}>
                        <RefreshCw className={cn("h-3.5 w-3.5", checking && "animate-spin")} /> {checking ? "Checking mailboxes…" : "Check replies"}
                    </Btn>
                </OutboundHeader>

                <div className="flex flex-wrap gap-1">
                    {FILTERS.map(([v, label]) => (
                        <button key={v} onClick={() => setIntent(v)}
                            className={cn("rounded-full px-3 py-1 text-xs border",
                                intent === v ? "border-primary bg-primary/10 text-primary" : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300")}>
                            {label}{v === "unclassified" && data?.unclassified ? ` (${data.unclassified})` : ""}
                        </button>
                    ))}
                </div>

                {!data ? <p className="text-xs text-gray-400">Loading…</p> : data.data.length === 0 ? (
                    <p className="text-xs text-gray-400 py-6 text-center">No replies here.</p>
                ) : (
                    <div className="space-y-3">
                        {data.data.map(r => (
                            <div key={r.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-2 text-xs">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{r.prospect_name}</p>
                                        <p className="text-gray-500">{r.from_email} · {r.received_at}{r.sequence_name && ` · ${r.sequence_name}`}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Pill tone={INTENT_TONE[r.intent || ""]} title={r.intent_reason}>
                                            {r.intent ? r.intent.replace(/_/g, " ") : "to classify"}{r.classified_by && ` · ${r.classified_by}`}
                                        </Pill>
                                        {r.customer_id && <button onClick={() => openLead(r.customer_id)} className="text-primary underline">Lead #{r.customer_id}</button>}
                                    </div>
                                </div>
                                <p className="font-medium text-gray-800 dark:text-gray-200">{r.subject}</p>
                                <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 max-h-48 overflow-auto">{r.body}</p>
                                {r.intent_reason && <p className="text-gray-500 italic">{r.intent_reason}</p>}
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Set intent:</span>
                                    <select value="" onChange={e => e.target.value && setReplyIntent(r, e.target.value)} className={cn(inputCls, "w-48")}>
                                        <option value="">Choose…</option>
                                        {(data.intents || []).map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                                    </select>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
